import { useCallback } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';

/**
 * Flattens an RHF errors object into the list of invalid field paths, in
 * validation order. Nested fields are returned as dotted paths.
 */
export const getErrorFieldNames = (errors: FieldErrors<FieldValues>, parentKey = ''): string[] => {
  const names: string[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (!value) continue;

    const path = parentKey ? `${parentKey}.${key}` : key;
    if (typeof value === 'object' && 'message' in value) {
      names.push(path);
      continue;
    }

    names.push(...getErrorFieldNames(value as FieldErrors<FieldValues>, path));
  }

  return names;
};

interface UseScrollToFirstErrorOptions {
  /**
   * Focus the first invalid field after scrolling to it. Disable for fields
   * whose focus has a side effect (e.g. an autocomplete that opens its panel).
   */
  focus?: boolean;
}

// Fields whose DOM id differs from the RHF field name (kept for automation/
// selector compatibility).
const FIELD_ID_OVERRIDES: Partial<Record<string, string>> = {
  OWNER_CENTER_CONSENT: 'elCheckbox',
};

/**
 * Focuses and scrolls to the first invalid field after a failed client-side
 * validation — the client-side counterpart to `useSetError`'s server-error
 * focus behavior. Field elements are located by their RHF field name (DOM id).
 */
export const useScrollToFirstError = ({ focus = true }: UseScrollToFirstErrorOptions = {}) =>
  useCallback(
    (errors: FieldErrors<FieldValues>) => {
      const [firstErrorField] = getErrorFieldNames(errors);
      if (!firstErrorField) return;

      const lastSegment = firstErrorField.split('.').pop() ?? firstErrorField;
      const elementId = FIELD_ID_OVERRIDES[firstErrorField] ?? lastSegment;
      const input = document.getElementById(elementId);
      if (!input) return;

      if (focus) {
        input.focus();
      }
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [focus]
  );

export default useScrollToFirstError;
